import { DataProcessingConfig, DataProcessingConfigSchema } from 'types'
import { z } from 'zod'

/**
 * Serialize the camelCase `DataProcessingConfig` (the *output* of
 * `DataProcessingConfigSchema`) back into the snake_case *input* the rolodex
 * `chart` entry stores.
 *
 * This mirrors `serializeSourceConfigs`/`serializeDateConfig` in
 * `chartSerialization.ts`. It exists because the schema is `.transform()`-based:
 * reads hand back camelCase, writes must send snake_case, and a camelCase write
 * silently fails `DataProcessingConfigSchema` on the next read.
 */
export const serializeDataProcessingConfigs = (
  configs: DataProcessingConfig[]
): z.input<typeof DataProcessingConfigSchema>[] =>
  configs.map((config) => {
    if (config.type === 'threshold_filter') {
      return {
        type: 'threshold_filter' as const,
        sensor_id: config.sensorId,
        enabled: config.enabled,
        condition: config.condition,
        value_1: config.value1,
        value_2: config.value2 ?? null,
      }
    }

    if (config.type === 'smoothing') {
      return {
        type: 'smoothing' as const,
        sensor_id: config.sensorId,
        enabled: config.enabled,
        window_size: config.windowSize,
        method: config.method,
        // omitted rather than nulled, since absence is what reads as legacy
        basis: config.basis ?? undefined,
        window_seconds: config.windowSeconds ?? undefined,
        alignment: config.alignment ?? undefined,
        display: config.display ?? undefined,
        require_full_window: config.requireFullWindow ?? undefined,
      }
    }

    if (config.type === 'resample') {
      return {
        type: 'resample' as const,
        sensor_id: config.sensorId,
        enabled: config.enabled,
        interval: config.interval,
        interval_unit: config.intervalUnit,
        aggregation_op: config.aggregationOp,
      }
    }

    return {
      type: 'regression' as const,
      regression_type: config.regressionType,
      degree: config.degree ?? null,
      id: config.id,
      enabled: config.enabled,
      x_variables: config.xVariables,
      y_variable: config.yVariable,
      end_date: config.endDate ?? null,
      min_x: config.minX ?? null,
      max_x: config.maxX ?? null,
      should_calculate: config.shouldCalculate ?? null,
    }
  })
